import React, { useEffect, useState, useCallback } from 'react';
import { tradingApi, type StrategySignalLog } from '../services/api';
import { Search, Activity, ChevronDown, ChevronUp, CheckCircle2, Info } from 'lucide-react';
import { CustomSelect } from './CustomElements';
import { formatDateTimeET } from '../utils/datetime';

interface SignalLogPanelProps {
    fullBleed?: boolean;
}

const SignalLogPanel: React.FC<SignalLogPanelProps> = ({ fullBleed = false }) => {
    const [signals, setSignals] = useState<StrategySignalLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [strategy, setStrategy] = useState<string>('');
    const [symbol, setSymbol] = useState<string>('');
    const [limit, setLimit] = useState(50);
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const fetchSignals = useCallback(async () => {
        setLoading(true);
        try {
            const response = await tradingApi.getStrategySignals({
                strategy: strategy || undefined,
                symbol: symbol || undefined,
                limit
            });
            if (response && response.success) {
                setSignals(response.data ?? []);
            }
        } catch (err) {
            console.error('Failed to fetch signals:', err);
        } finally {
            setLoading(false);
        }
    }, [strategy, symbol, limit]);

    useEffect(() => {
        fetchSignals();
        // Poll every 30s if looking at recent signals
        const id = setInterval(fetchSignals, 30000);
        return () => clearInterval(id);
    }, [fetchSignals]);

    const getSignalColor = (signal: string) => {
        switch (signal) {
            case 'BUY': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
            case 'SELL': return 'text-red-400 bg-red-500/10 border-red-500/20';
            default: return 'text-gray-400 bg-white/5 border-white/10';
        }
    };

    return (
        <div className={`panel flex flex-col h-[600px] ${fullBleed ? 'full-bleed-panel' : ''}`}>
            <div className="p-4 border-b border-white/10 bg-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-neon" />
                    <h2 className="text-md font-semibold text-white">Signal History</h2>
                    <span className="text-xs text-muted ml-2">Audit trail of strategy decisions</span>
                </div>
                <div className="flex items-center gap-3">
                    <CustomSelect
                        value={strategy}
                        onChange={setStrategy}
                        options={[
                            { value: '', label: 'All Strategies' },
                            { value: 'ON', label: 'Overnight (ON)' },
                            { value: 'ORB', label: 'ORB' },
                            { value: 'DT', label: 'Day Trade (DT)' },
                            { value: 'NQMM', label: 'Nasdaq Momentum (NQMM)' },
                        ]}
                        buttonClassName="bg-surface border-white/10 rounded-lg px-3 py-1 text-xs text-foreground h-8"
                        className="min-w-[150px]"
                        align="right"
                        ariaLabel="Strategy filter"
                    />
                    <input
                        type="text"
                        placeholder="Symbol..."
                        value={symbol}
                        onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                        className="bg-surface border border-white/10 rounded-lg px-3 py-1 text-xs text-foreground w-24 outline-none focus:border-neon/50"
                    />
                    <button
                        onClick={fetchSignals}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-muted transition"
                        title="Refresh"
                    >
                        <Search className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin">
                {loading && signals.length === 0 ? (
                    <div className="flex items-center justify-center p-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon"></div>
                    </div>
                ) : signals.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 text-muted">
                        <Info className="w-8 h-8 mb-2 opacity-20" />
                        <p>No signals found matching filters.</p>
                    </div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 bg-surface/90 backdrop-blur-md z-10">
                            <tr className="text-[10px] uppercase tracking-wider text-muted border-b border-white/5">
                                <th className="px-4 py-3 font-semibold">Time (NY)</th>
                                <th className="px-4 py-3 font-semibold">Strategy</th>
                                <th className="px-4 py-3 font-semibold">Symbol</th>
                                <th className="px-4 py-3 font-semibold">Signal</th>
                                <th className="px-4 py-3 font-semibold">Price</th>
                                <th className="px-4 py-3 font-semibold">Execution</th>
                                <th className="px-4 py-3 font-semibold"></th>
                            </tr>
                        </thead>
                        <tbody className="text-xs divide-y divide-white/5">
                            {signals.map((sig) => (
                                <React.Fragment key={sig.id}>
                                    <tr
                                        className={`group hover:bg-white/5 transition-colors cursor-pointer ${expandedId === sig.id ? 'bg-white/5' : ''}`}
                                        onClick={() => setExpandedId(expandedId === sig.id ? null : sig.id)}
                                    >
                                        <td className="px-4 py-3 text-muted whitespace-nowrap font-mono text-[11px]">
                                            {formatDateTimeET(sig.timestamp)}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-foreground">{sig.strategy}</td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-0.5 rounded bg-white/5 font-mono text-white/80">{sig.symbol}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${getSignalColor(sig.signal)}`}>
                                                {sig.signal}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-muted">
                                            ${sig.price?.toFixed(2) || '---'}
                                        </td>
                                        <td className="px-4 py-3">
                                            {sig.was_executed ? (
                                                <div className="flex items-center gap-1 text-emerald-400">
                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                    <span className="text-[10px] font-semibold uppercase tracking-tighter">Filled</span>
                                                </div>
                                            ) : (
                                                <span className="text-muted text-[10px] uppercase tracking-tighter opacity-50">Log Only</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {expandedId === sig.id ? <ChevronUp className="w-4 h-4 text-muted" /> : <ChevronDown className="w-4 h-4 text-muted opacity-0 group-hover:opacity-100" />}
                                        </td>
                                    </tr>
                                    {expandedId === sig.id && (
                                        <tr className="bg-white/5 border-x-2 border-neon/20">
                                            <td colSpan={7} className="px-8 py-4">
                                                <div className="space-y-3">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <p className="text-[10px] uppercase text-muted mb-1 font-bold tracking-widest">Reason</p>
                                                            <p className="text-foreground text-sm leading-relaxed">{sig.reason || 'N/A'}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-[10px] uppercase text-muted mb-1 font-bold tracking-widest">Signal ID</p>
                                                            <p className="text-muted font-mono">{sig.id}</p>
                                                        </div>
                                                    </div>

                                                    {sig.metadata_json && (
                                                        <div>
                                                            <p className="text-[10px] uppercase text-muted mb-2 font-bold tracking-widest">Technical Metadata</p>
                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-xl bg-black/40 border border-white/5 border-dashed">
                                                                {Object.entries(sig.metadata_json).map(([k, v]) => (
                                                                    typeof v !== 'object' ? (
                                                                        <div key={k}>
                                                                            <p className="text-[9px] text-muted-foreground uppercase opacity-70 mb-0.5">{k.replace(/_/g, ' ')}</p>
                                                                            <p className="font-mono text-white/90">{typeof v === 'number' ? v.toFixed(4) : String(v)}</p>
                                                                        </div>
                                                                    ) : null
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="p-3 border-t border-white/10 bg-surface/50 flex justify-between items-center">
                <p className="text-[10px] text-muted italic">Showing latest {signals.length} evaluations</p>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted uppercase font-bold pr-1">Show</span>
                    {[25, 50, 100].map(val => (
                        <button
                            key={val}
                            onClick={() => setLimit(val)}
                            className={`w-7 h-7 rounded flex items-center justify-center text-[10px] font-bold border transition ${limit === val ? 'bg-neon/20 border-neon/40 text-neon' : 'bg-white/5 border-white/10 text-muted hover:text-white'}`}
                        >
                            {val}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SignalLogPanel;
